/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { ValidationErrorTypes, ERROR_CONFIG_SEPARATOR } from '@/auth/types';

/* eslint-disable no-useless-escape */
type ValidationConfig = Record<string, string>;
type TranslationList = Record<string, string>;
type ErrorsList = Record<string, string>;
export type ValidationFieldsConfig = {
  validateRules: Record<string, string>[];
  code?: string;
  customUpperCode: string;
  required: boolean;
};

enum ConfigEnumLength {
  MIN_TEXT_LENGTH = 'MIN_TEXT_LENGTH',
  MAX_TEXT_LENGTH = 'MAX_TEXT_LENGTH',
  DATE_RANGE_MIN = 'DATE_RANGE_MIN',
  DATE_RANGE_MAX = 'DATE_RANGE_MAX',
}

export enum InputValidation {
  Numeric = 'numeric',
  AlphanumWithSpaces = 'alphanum-with-spaces',
  Alphanumeric = 'alphanumeric',
  Alpha = 'alpha',
  Email = 'email',
  Length = 'length',
  Date = 'date',
  Url = 'url',
}

const flattenObjectsArray = (arr: ValidationConfig[]): ValidationConfig => {
  return arr.reduce((acc, obj) => {
    return { ...acc, [obj.name]: obj.value };
  }, {});
};

//The basic material for the functions responsible for validation was taken from https://github.com/magento/magento2/blob/2.4/app/code/Magento/Ui/view/base/web/js/lib/validation/rules.js
export const validateNumeric = (value: string): boolean => /^\d+$/.test(value);

export const validateAlphanumWithSpaces = (value: string): boolean =>
  /^[a-zA-Z0-9\s]+$/.test(value);

export const validateAlphanumeric = (value: string): boolean =>
  /^[a-zA-Z0-9]+$/.test(value);

export const validateAlpha = (value: string): boolean =>
  /^[a-zA-Z]+$/.test(value);

export const validateEmail = (value: string): boolean =>
  /^[a-z0-9,!\#\$%&'\*\+\/=\?\^_`\{\|\}~-]+(\.[a-z0-9,!\#\$%&'\*\+\/=\?\^_`\{\|\}~-]+)*@([a-z0-9-]+\.)+[a-z]{2,}$/i.test(
    value
  );

export const validateDate = (value: string): boolean =>
  /^\d{4}-\d{2}-\d{2}$/.test(value) && !isNaN(Date.parse(value));

export const isDateWithinRange = (
  date: string,
  minTimestamp?: number,
  maxTimestamp?: number
): boolean => {
  const dateTimestamp = new Date(date).getTime() / 1000;

  if (isNaN(dateTimestamp) || dateTimestamp < 0) {
    return false;
  }

  if (typeof minTimestamp !== 'undefined' && dateTimestamp < minTimestamp) {
    return false;
  }

  if (typeof maxTimestamp !== 'undefined' && dateTimestamp > maxTimestamp) {
    return false;
  }

  return true;
};

export const convertTimestampToDate = (
  timestamp: string | undefined | null
): string => {
  if (!timestamp || timestamp.trim() === '') return '';

  const parsedTimestamp = parseInt(timestamp, 10);

  if (!isNaN(parsedTimestamp)) {
    const date = new Date(parsedTimestamp * 1000);

    if (isNaN(date.getTime())) return '';

    return date.toISOString().split('T')[0];
  }

  const isoDate = new Date(timestamp);

  if (isNaN(isoDate.getTime())) return '';

  const month = parseInt(timestamp.split('-')[1], 10);

  if (month > 12 || month < 1) return '';

  return isoDate.toISOString().split('T')[0];
};

export const validateUrl = (url: string) => {
  const urlPattern =
    /^(https?|ftp):\/\/(([A-Z0-9]([A-Z0-9_-]*[A-Z0-9]|))(\.[A-Z0-9]([A-Z0-9_-]*[A-Z0-9]|))*)(:(\d+))?(\/[A-Z0-9~](([A-Z0-9_~-]|\.)*[A-Z0-9~]|))*\/?(.*)?$/i;
  return urlPattern.test(url);
};

export const validateLength = (
  value: string,
  minLength: number,
  maxLength: number
): boolean => {
  const length = value.length;
  return length >= minLength && length <= maxLength;
};

export const validationFields = (
  value: string,
  configs: ValidationFieldsConfig,
  translations: TranslationList,
  errorsList: ErrorsList
) => {
  const {
    requiredFieldError,
    lengthTextError,
    numericError,
    alphaNumWithSpacesError,
    alphaNumericError,
    alphaError,
    emailError,
    dateError,
    urlError,
    dateRangeError,
    dateMaxError,
    dateMinError,
  } = translations;

  const fieldName = configs?.customUpperCode as string;

  const defaultFields = { [fieldName]: '' };

  if (errorsList[fieldName]) {
    delete errorsList[fieldName];
  }

  if (configs?.required && (!value || value === 'false')) {
    return {
      [fieldName]: `${ValidationErrorTypes.REQUIRED_FIELD_ERROR}${ERROR_CONFIG_SEPARATOR}${requiredFieldError}`,
    };
  }

  if (!configs?.required && !value) {
    return defaultFields;
  }

  if (!configs?.validateRules?.length) return defaultFields;

  const validateRulesConfig = flattenObjectsArray(configs?.validateRules);

  const min = validateRulesConfig[ConfigEnumLength.MIN_TEXT_LENGTH] ?? 1;
  const max = validateRulesConfig[ConfigEnumLength.MAX_TEXT_LENGTH] ?? 255;
  const dateMin = validateRulesConfig[ConfigEnumLength.DATE_RANGE_MIN];
  const dateMax = validateRulesConfig[ConfigEnumLength.DATE_RANGE_MAX];

  if (!validateLength(value, +min, +max) && !(dateMin || dateMax)) {
    const parsedErrorMessage = lengthTextError
      .replace('{min}', min)
      .replace('{max}', max);

    return {
      [fieldName]: `${ValidationErrorTypes.LENGTH_TEXT_ERROR}${ERROR_CONFIG_SEPARATOR}${parsedErrorMessage}`,
    };
  }

  if (!isDateWithinRange(value, +dateMin, +dateMax) && (dateMin || dateMax)) {
    if (dateMin && dateMin) {
      const parsedErrorMessage = dateRangeError
        .replace('{min}', convertTimestampToDate(dateMin))
        .replace('{max}', convertTimestampToDate(dateMax));

      return {
        [fieldName]: `${ValidationErrorTypes.DATE_RANGE_ERROR}${ERROR_CONFIG_SEPARATOR}${parsedErrorMessage}`,
      };
    }

    if (typeof dateMin === 'undefined' && typeof dateMax !== 'undefined') {
      const parsedErrorMessage = dateMaxError.replace(
        '{max}',
        convertTimestampToDate(dateMax)
      );

      return {
        [fieldName]: `${ValidationErrorTypes.DATE_MAX_ERROR}${ERROR_CONFIG_SEPARATOR}${parsedErrorMessage}`,
      };
    }

    if (typeof dateMax === 'undefined' && typeof dateMin !== 'undefined') {
      const parsedErrorMessage = dateMinError.replace(
        '{min}',
        convertTimestampToDate(dateMin)
      );

      return {
        [fieldName]: `${ValidationErrorTypes.DATE_MIN_ERROR}${ERROR_CONFIG_SEPARATOR}${parsedErrorMessage}`,
      };
    }
  }

  const validationMap = {
    [InputValidation.Numeric]: {
      validate: validateNumeric,
      error: `${ValidationErrorTypes.NUMERIC_ERROR}${ERROR_CONFIG_SEPARATOR}${numericError}`,
    },
    [InputValidation.AlphanumWithSpaces]: {
      validate: validateAlphanumWithSpaces,
      error: `${ValidationErrorTypes.ALPHA_NUM_WITH_SPACES_ERROR}${ERROR_CONFIG_SEPARATOR}${alphaNumWithSpacesError}`,
    },
    [InputValidation.Alphanumeric]: {
      validate: validateAlphanumeric,
      error: `${ValidationErrorTypes.ALPHA_NUMERIC_ERROR}${ERROR_CONFIG_SEPARATOR}${alphaNumericError}`,
    },
    [InputValidation.Alpha]: {
      validate: validateAlpha,
      error: `${ValidationErrorTypes.ALPHA_ERROR}${ERROR_CONFIG_SEPARATOR}${alphaError}`,
    },
    [InputValidation.Email]: {
      validate: validateEmail,
      error: `${ValidationErrorTypes.EMAIL_ERROR}${ERROR_CONFIG_SEPARATOR}${emailError}`,
    },
    [InputValidation.Date]: {
      validate: validateDate,
      error: `${ValidationErrorTypes.DATE_ERROR}${ERROR_CONFIG_SEPARATOR}${dateError}`,
    },
    [InputValidation.Url]: {
      validate: validateUrl,
      error: `${ValidationErrorTypes.URL_ERROR}${ERROR_CONFIG_SEPARATOR}${urlError}`,
    },
  };

  const validation =
    validationMap[
      validateRulesConfig['INPUT_VALIDATION'] as keyof typeof validationMap
    ];

  if (
    validation &&
    !validation.validate(value) &&
    !errorsList[fieldName]?.length
  ) {
    return { [fieldName]: validation.error };
  }

  return defaultFields;
};
